import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { CalendarIcon, DragHandleDots2Icon, Pencil1Icon, TrashIcon } from '@radix-ui/react-icons'
import { Badge, Flex, IconButton, Text } from '@radix-ui/themes'
import { useState } from 'react'
import { PromotionCampaign } from '@Generated/back-office-client/src'
import { formatDateToMonthDayYearString } from '@Components/util/utils'

type BadgeColor = 'green' | 'blue' | 'gray' | 'red' | 'yellow' | 'orange'

type PromotionCampaignCardProps = {
  campaign: PromotionCampaign
  status: string
  statusColor: BadgeColor
  timezone: string
  isDraggable?: boolean
  onEdit: () => void
  onDelete: () => void
}

export const PromotionCampaignCard = ({
  campaign,
  status,
  statusColor,
  timezone,
  isDraggable = true,
  onEdit,
  onDelete,
}: PromotionCampaignCardProps) => {
  const [hovered, setHovered] = useState(false)
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: campaign.id,
    disabled: !isDraggable,
  })

  // Only keep dynamic styles that DnD library needs
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const cardClassName = isDraggable ? 'promotion-card' : 'promotion-card promotion-card--disabled'

  const formatDate = (date: Date | null | undefined) => {
    if (!date) {
      return 'Not set'
    }
    return formatDateToMonthDayYearString(new Date(date), timezone)
  }

  return (
    <Flex
      ref={setNodeRef}
      className={cardClassName}
      style={style}
      {...attributes}
      {...listeners}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      align="center"
      gap="3">
      {isDraggable && (
        <Flex>
          <DragHandleDots2Icon width="20" height="20" className="promotion-card__icon" />
        </Flex>
      )}

      <Flex direction="column" gap="1" className="promotion-card__content">
        <Flex align="center" gap="2">
          <Text size="3" weight="bold">
            {campaign.name}
          </Text>
          <Badge color={statusColor} size="1">
            {status}
          </Badge>
        </Flex>
        <Flex align="center" gap="1">
          <CalendarIcon width="14" height="14" className="promotion-card__icon" />
          <Text size="2" color="gray">
            {formatDate(campaign.startAt)} - {formatDate(campaign.endAt)}
          </Text>
        </Flex>
      </Flex>

      {hovered && (
        <Flex
          align="center"
          gap="2"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}>
          <IconButton variant="ghost" onClick={onEdit} title="Edit campaign">
            <Pencil1Icon />
          </IconButton>
          <IconButton variant="ghost" color="red" onClick={onDelete} title="Delete campaign">
            <TrashIcon />
          </IconButton>
        </Flex>
      )}
    </Flex>
  )
}
